import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import WidgetCard from './WidgetCard'
import { Pill } from '../../../layout/styledComponents'
import { parseUtcDate } from '../../../utils/dates'

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${(p) =>
    p.$status === 'TAKEN' ? p.theme?.color?.accentSoft || '#E1ECE7' : p.theme?.color?.bgSubtle || '#F4F0E9'};
  border: 1px solid ${(p) => (p.$overdue ? p.theme?.color?.urgent || '#C24D49' : 'transparent')};
`

const Check = styled.button`
  appearance: none;
  border: 2px solid ${(p) => p.theme?.color?.borderStrong || '#D5CCBC'};
  background: ${(p) => (p.$on ? p.theme?.color?.accent || '#6B9080' : 'transparent')};
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  flex-shrink: 0;
  &:hover { border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'}; }
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
`

const Name = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const Sub = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

function timeLabel(iso) {
  if (!iso) return ''
  try {
    return parseUtcDate(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch (e) {
    return iso
  }
}

function keyForDose(d) {
  // Maps a dose to the mock-state flag used in todayApi.js. Keeps the demo
  // reactive without inventing new ids per dose.
  if (d.medicineId === 'med_ssri' && timeLabel(d.scheduledTimeLabel).includes('8'))
    return 'med_ssri_morning_taken'
  if (d.medicineId === 'med_b12') return 'med_b12_taken'
  if (d.medicineId === 'med_metformin' && timeLabel(d.scheduledTimeLabel).includes('8'))
    return 'med_metformin_morning_taken'
  if (d.medicineId === 'med_amlodipine') return 'med_amlodipine_taken'
  return null
}

export default function MedicinesDueCard({ data, onMarkTaken }) {
  const navigate = useNavigate()
  if (!data || data.length === 0) return null

  const pendingCount = data.filter((d) => d.status === 'PENDING').length
  return (
    <WidgetCard
      title="Medicines today"
      badge={
        pendingCount === 0
          ? 'All taken'
          : `${pendingCount} pending`
      }
    >
      <List>
        {data.map((d, i) => {
          const taken = d.status === 'TAKEN'
          const stateKey = keyForDose(d)
          return (
            <Row key={`${d.medicineId}-${i}`} $status={d.status} $overdue={d.isOverdue}>
              <Check
                $on={taken}
                aria-label={taken ? `${d.name} taken` : `Mark ${d.name} taken`}
                onClick={() => stateKey && !taken && onMarkTaken && onMarkTaken(stateKey)}
              >
                {taken ? '✓' : ''}
              </Check>
              <Body>
                <Name>
                  {d.name}{' '}
                  <span style={{ color: '#6B7280', fontWeight: 400 }}>· {d.doseLabel}</span>
                </Name>
                <Sub>
                  {timeLabel(d.scheduledTimeLabel) || 'Anytime'}
                  {d.isOverdue && !taken ? ' · overdue' : ''}
                </Sub>
              </Body>
              {d.isOverdue && !taken && <Pill $tone="urgent">Late</Pill>}
            </Row>
          )
        })}
      </List>
      <button
        onClick={() => navigate('/patient-home/care/medicines')}
        style={{
          marginTop: 14,
          background: 'transparent',
          border: 'none',
          color: '#1F3A5F',
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
          padding: 0,
          fontSize: 14,
        }}
      >
        See all medicines →
      </button>
    </WidgetCard>
  )
}
